import React, {
  memo,
  useRef,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { isNil } from 'ramda';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import debounce from 'lodash.debounce';
import { VariableSizeGrid as Grid } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import WindowScroller from '../../../utils/external/WindowScroller';
import GameCard from '../GameCard/GameCardBase';
import { usePrevious } from '../../../hooks/useCompare';
import { useIsSSCOMBrand } from '../../../hooks/useIsSSCOMBrand';
import { useCasinoAppStoreReviewMode } from '../../../hooks/useAppStoreReviewMode';
import { useAppStoreCommonGamesFilter } from '../../../hooks/useAppStoreGamesFilter';
import { readCSSVar } from '../../../utils/cssVarManipulation';
import styles from './GameCardsGridVirtualized.module.css';

const Cell = memo(({ columnIndex, rowIndex, data, style }) => {
  const { items, columnCount, cellSpacing, isItemLoaded, itemProps } = data;

  const index = columnIndex + rowIndex * columnCount;
  const item = items[index];
  const isExist = Boolean(item);

  const width = style.width - cellSpacing;
  const height = style.height - cellSpacing;

  const cellStyles = {
    ...style,
    width,
    height,
  };

  return (
    <li className={styles.item} style={cellStyles}>
      {isItemLoaded(index) ? (
        isExist && <GameCard game={item} index={index} {...itemProps} />
      ) : (
        <GameCard isLoading />
      )}
    </li>
  );
});

const defaultGridParams = {
  cellWidth: 142,
  cellHeight: 142,
  cellSpacing: 12,
  columnCount: 2,
  rowCount: 0,
  rowWidth: 288,
  layoutIndent: 12,
};

const itemsRenderedAdapter = (gridProps, columnCount = 1) => ({
  overscanStartIndex: gridProps.overscanRowStartIndex * columnCount,
  overscanStopIndex: gridProps.overscanRowStopIndex * columnCount,
  visibleStartIndex: gridProps.visibleRowStartIndex * columnCount,
  visibleStopIndex: gridProps.visibleRowStopIndex * columnCount,
});

const GameCardsGridVirtualized = ({
  items,
  limit,
  itemProps,
  isLoading,
  loadMoreGames,
  fixedRowIndex,
  overscanRowCount,
  ...props
}) => {
  const isSCCOM = useIsSSCOMBrand();

  const listRef = useRef();
  const componentRef = useRef();
  const fixedRowRef = useRef(null);

  const [isReady, setIsReady] = useState(false);
  const [gridParams, setGridParams] = useState(defaultGridParams);
  const prevWidth = usePrevious(window.innerWidth);

  const isCasinoAppStoreReviewMode = useCasinoAppStoreReviewMode();
  const appStoreReviewFilter = useAppStoreCommonGamesFilter();

  const collection = appStoreReviewFilter(items);

  const itemCount = useMemo(
    () => (isLoading ? collection.length + limit / 2 : collection.length),
    [isLoading, collection, limit]
  );

  const isItemLoaded = useCallback(
    (index) => !isLoading || index < collection.length,
    [isLoading, collection]
  );

  const updateGridParams = useCallback(
    (node) => {
      const columnCount = parseInt(readCSSVar(node, '--grid-column-count'));
      const cellSpacing = parseInt(readCSSVar(node, '--grid-item-spacing'));
      const layoutIndent = parseInt(readCSSVar(node, '--grid-layout-indent'));
      const cellWidth = parseInt(readCSSVar(node, '--grid-item-width'));
      const cellItemHeight = parseInt(readCSSVar(node, '--grid-item-height'));
      const cellTitleHeight = parseInt(readCSSVar(node, '--grid-item-title'));
      const layoutWidth = parseInt(readCSSVar(node, '--grid-row-width'));

      const rowCount = Math.ceil(itemCount / columnCount);
      const rowWidth = layoutWidth + layoutIndent * 2;
      const cellHeight = isSCCOM
        ? cellItemHeight
        : cellTitleHeight + cellItemHeight;

      setGridParams({
        cellWidth,
        cellHeight,
        cellSpacing,
        columnCount,
        rowCount,
        rowWidth,
        layoutIndent,
      });
    },
    [itemCount, setGridParams, isSCCOM]
  );

  const componentDidMount = useCallback(
    (node) => {
      if (node) {
        componentRef.current = node;
        updateGridParams(node);
        setIsReady(true);
      }
    },
    [updateGridParams]
  );

  const scrollHandler = useCallback(
    (scrollPosition) => listRef?.current?.scrollTo?.(scrollPosition),
    [listRef]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resizeHandler = useCallback(
    debounce((force) => {
      // Prevent updating Grid params for vertical resize
      if (prevWidth === window.innerWidth && !force) return;

      setIsReady(false);
      updateGridParams(componentRef.current);
      setIsReady(true);
    }, 200),
    [setIsReady, updateGridParams, prevWidth]
  );

  useEffect(() => {
    if (
      (fixedRowRef.current !== fixedRowIndex && !isNil(fixedRowIndex)) ||
      !fixedRowIndex
    ) {
      fixedRowRef.current = fixedRowIndex;
      resizeHandler(true);
    }
  }, [fixedRowIndex, resizeHandler]);

  useEffect(() => {
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
  }, [resizeHandler]);

  const {
    cellWidth,
    cellHeight,
    cellSpacing,
    columnCount,
    rowCount,
    rowWidth,
    layoutIndent,
  } = gridParams;

  const getCellWidth = useCallback(() => {
    return cellWidth + cellSpacing;
  }, [cellWidth, cellSpacing]);
  const getCellHeight = useCallback(
    () => cellHeight + cellSpacing,
    [cellHeight, cellSpacing]
  );

  return (
    <div
      ref={componentDidMount}
      className={classNames(
        styles.component,
        isCasinoAppStoreReviewMode && styles.casinoAppReview
      )}
      {...props}
    >
      <WindowScroller onScroll={scrollHandler}>{() => <div />}</WindowScroller>

      {isReady && (
        <InfiniteLoader
          loadMoreItems={loadMoreGames}
          isItemLoaded={isItemLoaded}
          itemCount={itemCount}
          minimumBatchSize={limit}
        >
          {({ onItemsRendered, ref }) => (
            <Grid
              ref={(component) => {
                ref(component);
                listRef.current = component;
              }}
              onItemsRendered={(gridProps) => {
                onItemsRendered(itemsRenderedAdapter(gridProps, columnCount));
              }}
              rowCount={rowCount}
              columnCount={columnCount}
              rowHeight={getCellHeight}
              columnWidth={getCellWidth}
              width={rowWidth}
              height={window.innerHeight}
              overscanRowCount={overscanRowCount}
              overscanColumnCount={columnCount}
              innerElementType="ul"
              itemData={{
                items: collection,
                columnCount,
                cellSpacing,
                isItemLoaded,
                itemProps,
              }}
              style={{ margin: `0 ${layoutIndent}px` }}
              className={styles.list}
            >
              {Cell}
            </Grid>
          )}
        </InfiniteLoader>
      )}
    </div>
  );
};

GameCardsGridVirtualized.propTypes = {
  items: PropTypes.array,
  itemProps: PropTypes.shape({}),
  limit: PropTypes.number,
  isLoading: PropTypes.bool,
  // About below props read more here
  // https://www.npmjs.com/package/react-window-infinite-loader
  loadMoreGames: PropTypes.func,
  overscanRowCount: PropTypes.number,
};

GameCardsGridVirtualized.defaultProps = {
  items: [],
  itemProps: {},
  limit: 0,
  isLoading: false,
  loadMoreGames: () => null,
  overscanRowCount: 30,
};

export default GameCardsGridVirtualized;
